// MACHINERY V2 (Stroje) — M2.1 + M2.9 QA gallery (TEMPORARY SCAFFOLD).
//
// WHY THIS FILE EXISTS: react-router v6 does not create a match branch for a
// pathless layout route that has no children, so the v2 layout needs at least
// one child route element to mount against. At M2.0 that child was a palette
// smoke test; at M2.1 it became the QA gallery for the locked 9-state card
// contract (§4.3) — every state side by side, under BOTH palettes, so the
// owner can eyeball the whole contract on .cc before fleet consumes it. M2.9
// appends the readiness gallery below it (ReadinessQaGallery.tsx, same
// delete-at-M2.2 fate) so both contracts are verifiable on one screen.
//
// ChromeNavV4 is NOT rendered here: it mounts in MachineryV2Layout ABOVE the
// outlet (it is page chrome, and the permanent platform nav from M2.12), so
// it already sits on screen above this gallery. Nav + all 9 states are
// therefore visible together on one screen, which is the point of the gallery.
//
// DELETE AT M2.2: this element is the `path=""` child of the v2 block and gets
// swapped for `fleet/FleetV1Page.tsx`. Nothing here may survive into the
// cutover (M2.12) — the mock machines below are display-only fixtures and are
// NOT a data contract; the typed model they build lives in fleet/cardStates.ts.
//
// Copy is intentionally NOT run through i18next: developer-facing scaffold,
// deleted before any user-facing label audit (M2.12 §14). The MACHINE copy in
// the fixtures is Slovak because it is transcribed verbatim from the designer
// artboard (machinery-states.jsx STATE_SPECS) so the gallery matches it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { MachineryV2Palette, setPalette } from "./machineryV2UiSlice";
import ReadinessQaGallery from "./ReadinessQaGallery";
import StateCard from "./fleet/StateCard";
import {
  MachineryV2CardMachine,
  MachineryV2Telemetry,
  deriveCardState,
  unconnectedTelemetry,
} from "./fleet/cardStates";

const PALETTES: { id: MachineryV2Palette; label: string }[] = [
  { id: "claude", label: "Refined Claude" },
  { id: "machineid", label: "MachineID Native" },
];

const tel = (p: Partial<MachineryV2Telemetry>): MachineryV2Telemetry => ({
  ...unconnectedTelemetry(),
  ...p,
});

const machine = (
  id: string,
  brand: string,
  model: string,
  typeLabel: string,
  serialNumber: string,
  controlSystem: string,
  locationLabel: string,
  subLocationLabel: string,
  telemetry: MachineryV2Telemetry
): MachineryV2CardMachine => ({
  id,
  brand,
  model,
  typeLabel,
  serialNumber,
  controlSystem,
  locationLabel,
  subLocationLabel,
  telemetry,
});

interface GalleryEntry {
  n: string;
  title: string;
  note: string;
  m: MachineryV2CardMachine;
}

const GALLERY: GalleryEntry[] = [
  {
    n: "01",
    title: "Unconnected",
    note: "No MachineConnection row — 99% of the fleet. No status badge, no conn dot; the footer says BEZ TELEMETRIE so the absence is explicit.",
    m: machine("s1", "+GF+", "MIKRON HPM 1200HD", "3-Axis CNC Machining Center", "123123", "Heidenhain", "Závod Brno 1", "Hala B", tel({})),
  },
  {
    n: "02",
    title: "Awaiting agent",
    note: "Connection row exists, agent has never sent data. Dashed teal badge, no conn dot (there is no heartbeat to grade yet).",
    m: machine("s2", "DMG MORI", "NHX 5000", "Horizontal CNC Machining Center", "990012", "Heidenhain", "Závod Brno 1", "Hala A", tel({ conn: "awaiting" })),
  },
  {
    n: "03",
    title: "Running · healthy",
    note: "Emerald badge + play icon, live conn dot (pulsing), program line at the foot.",
    m: machine("s3", "Makino", "a51nx-5XU", "5-Axis CNC Machining Center", "34234", "FANUC", "Závod Brno 1", "Hala A", tel({ conn: "live", status: "running", since: "06:49", elapsed: "2h 14m", heartbeat: "pred 4 s", program: "PRG_42.MPF", partCount: 247, spindleRpm: 11800, feedPercent: 95 })),
  },
  {
    n: "04",
    title: "Idle · healthy",
    note: "Amber pause badge, live conn dot. No program line — nothing is cutting.",
    m: machine("s4", "DMG MORI", "DMC 100 U", "5-Axis CNC Machining Center", "SN-30886", "Siemens", "Závod Brno 1", "Hala A", tel({ conn: "live", status: "idle", since: "08:42", elapsed: "1h 18m", heartbeat: "pred 41 s" })),
  },
  {
    n: "05",
    title: "Stopped · healthy",
    note: "Grey square badge, live conn dot. Intentionally off — planned downtime, end of shift.",
    m: machine("s5", "EiMa", "Gamma T Linear", "5-Axis CNC Machining Center", "3246", "Siemens", "Závod Trenčín", "Výrobňa", tel({ conn: "live", status: "stopped", heartbeat: "pred 6 min" })),
  },
  {
    n: "06",
    title: "Alarm · healthy",
    note: "Red triangle badge, live conn dot, red alarm strip (code · message · since). NEVER animates.",
    m: machine("s6", "Trens", "SBL 500 CNC", "2-Axis CNC Turning Machine", "67844", "Siemens", "Závod Brno 1", "Hala B", tel({ conn: "live", status: "alarm", since: "23:18", heartbeat: "pred 7 s", alarms: [{ code: "Alarm 700057", message: "Nízka hladina v hydraulickom agregáte", since: "23:18" }] })),
  },
  {
    n: "07",
    title: "Stale (last-known)",
    note: "Heartbeat past the window: badge desaturates + gains ?, conn dot goes amber. Both degrade together — one input (conn) drives both.",
    m: machine("s7", "+GF+", "MIKRON HPM 1200HD", "3-Axis CNC Machining Center", "685868", "Heidenhain", "Závod Trenčín", "Výrobňa", tel({ conn: "stale", status: "running", since: "06:03", elapsed: "3h 00m", heartbeat: "pred 4 min", program: "HRUB_03.MPF", partCount: 4, spindleRpm: 18000, feedPercent: 80 })),
  },
  {
    n: "08",
    title: "Stale + alarm",
    note: "Desaturated red badge + ?, amber conn dot, alarm strip prefixed (naposledy) so an old fault never reads as live.",
    m: machine("s8", "Biesse", "Materia CL 30.15", "5-Axis CNC Machining Center", "6846", "Siemens", "Závod Brno 1", "Hala A", tel({ conn: "stale", status: "alarm", since: "04:12", heartbeat: "pred 8 min", alarms: [{ code: "Alarm 1023", message: "Tlak v upínači mimo toleranciu", since: "04:12" }] })),
  },
  {
    n: "09",
    title: "Offline (server-flipped)",
    note: "Server flipped the status after ~60 s of silence. Slate badge + cloud-off icon, grey conn dot with an X knockout.",
    m: machine("s9", "+GF+", "MIKRON HSM 400U", "5-Axis CNC Grinding Machine", "44910", "Heidenhain", "Závod Brno 1", "Hala B", tel({ conn: "longstale", status: "offline", since: "07:48", heartbeat: "pred 2 h 14 min" })),
  },
  // --- Branches added by the M2.1 fix pass + the 2026-08-22 amendment that
  // had no visual fixture until M2.9. Display-only, same StateCard + contract.
  {
    n: "10",
    title: "Connected, status unreadable",
    note: "Heartbeat is live but the adapter cannot map a status. Resolves to AWAITING, not unconnected — the agent is reachable, so claiming no telemetry would mis-file a live machine for the M2.2 filters.",
    m: machine("s10", "Doosan", "Puma 2600", "2-Axis CNC Turning Machine", "77120", "FANUC", "Závod Trenčín", "Hala C", tel({ conn: "live", status: null, heartbeat: "pred 3 s" })),
  },
  {
    n: "11",
    title: "Running + alarms → ALARM",
    note: "The status field still says running while alarms are active. The alarm payload outranks the STATUS FIELD, so the badge is red and the strip is live — no green RUNNING badge above a red strip.",
    m: machine("s11", "Makino", "a51nx-5XU", "5-Axis CNC Machining Center", "34234", "FANUC", "Závod Brno 1", "Hala A", tel({ conn: "live", status: "running", since: "05:40", elapsed: "3h 21m", heartbeat: "pred 5 s", program: "PRG_42.MPF", partCount: 88, spindleRpm: 9400, feedPercent: 92, alarms: [{ code: "Alarm 700221", message: "Preťaženie vretena", since: "08:12" }] })),
  },
  {
    n: "12",
    title: "Offline + alarms → OFFLINE",
    note: "A connectivity VERDICT outranks alarms for the STATE, so this stays offline and the M2.2 offline filter cannot miss it. The badge keeps the slate cloud-off treatment (never desaturated red, no ? marker) and the alarm is carried forward as (naposledy).",
    m: machine("s12", "Biesse", "Materia CL 30.15", "5-Axis CNC Machining Center", "6846", "Siemens", "Závod Brno 1", "Hala A", tel({ conn: "longstale", status: "offline", since: "02:55", heartbeat: "pred 3 h 08 min", alarms: [{ code: "Alarm 1023", message: "Tlak v upínači mimo toleranciu", since: "02:55" }] })),
  },
];

const MachineryV2FoundationShell: React.FC = () => {
  const dispatch = useAppDispatch();
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "22px 28px 26px" }}>
      <div className="eyebrow" style={{ color: "var(--mod-accent)" }}>
        Stroje · Machinery v2 · M2.1 QA gallery
      </div>
      <h1
        style={{
          margin: "4px 0 0",
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: "var(--ink)",
        }}
      >
        9 canonical card states
      </h1>
      <p style={{ margin: "8px 0 0", maxWidth: 720, color: "var(--muted)" }}>
        Display-only fixtures rendered through the locked contract in
        fleet/cardStates.ts. Every state carries a colour AND an icon;
        unconnected renders no badge and no dot; stale desaturates the badge and
        ambers the dot together; the program line appears only when running; the
        alarm strip never animates. Tiles 10-12 cover the derivation branches
        that had no fixture before: connected-but-unreadable, running+alarms,
        and offline+alarms (state stays OFFLINE, alarm carried as last-known).
        The bar above is ChromeNavV4. Check both palettes.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "18px 0 0",
          flexWrap: "wrap",
        }}
      >
        <span className="eyebrow">Palette</span>
        {PALETTES.map((p) => (
          <button
            key={p.id}
            type="button"
            className={palette === p.id ? "btn btn-mod" : "btn"}
            onClick={() => dispatch(setPalette(p.id))}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 20,
          marginTop: 22,
        }}
      >
        {GALLERY.map((entry) => (
          <div
            key={entry.n}
            style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--mod-accent)",
                  letterSpacing: "0.1em",
                }}
              >
                {entry.n}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                {entry.title}
              </span>
              {/* QA readout: proves deriveCardState resolves the intended
                  state from the telemetry alone. */}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9.5,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                }}
              >
                {deriveCardState(entry.m.telemetry)}
              </span>
            </div>
            <StateCard machine={entry.m} />
            <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.45 }}>
              {entry.note}
            </div>
          </div>
        ))}
      </div>

      <ReadinessQaGallery />
    </div>
  );
};

export default MachineryV2FoundationShell;
